import type { Tenant } from '@/lib/bff/tenants'

export type TenantResolution =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'welcome' }
  /** `fromHeld` is a reload in the same session, which keeps the current URL. */
  | { kind: 'open'; tenant: Tenant; fromHeld: boolean }
  /** `lostIdle` means an idle-restore tenant is no longer allowed; it is never named. */
  | { kind: 'choose'; lostIdle: boolean }

/*
 * Decides how a signed-in user enters a tenant (VIS-326 AC 3, AC 4). Applied
 * in order: a held tenant from this session (reload), then a one-shot idle
 * restore, then the ordinary rules over the tenants that remain.
 */
export function resolveTenant({
  tenants,
  error,
  heldTenantId,
  idleTenantId,
}: {
  tenants: Tenant[] | undefined
  error?: unknown
  heldTenantId: string | null
  idleTenantId: string | null
}): TenantResolution {
  if (error) return { kind: 'error' }
  if (!tenants) return { kind: 'loading' }

  const held = heldTenantId ? tenants.find((tenant) => tenant.id === heldTenantId) : undefined
  if (held) return { kind: 'open', tenant: held, fromHeld: true }

  const idle = idleTenantId ? tenants.find((tenant) => tenant.id === idleTenantId) : undefined
  if (idle) return { kind: 'open', tenant: idle, fromHeld: false }

  if (tenants.length === 0) return { kind: 'welcome' }
  if (tenants.length === 1) return { kind: 'open', tenant: tenants[0], fromHeld: false }
  return { kind: 'choose', lostIdle: Boolean(idleTenantId) }
}
